import firebaseService from '@services/firebase';
import {
  IUserPreferences,
  UserPreferencesSubDocument,
} from '@monofront/business/interfaces';
import {
  COLLECTIONS_PREFERENCES_DOCUMENT_NAME,
  PAYMENT_PREFERENCES_DOCUMENT_NAME,
  PREFERRED_PREFERENCES_COLLECTION_NAME,
} from '@monofront/business/constants';
import { doc, getDoc, setDoc, updateDoc } from '@firebase/firestore';

const DEFAULT_PREFERENCES: IUserPreferences = {
  [COLLECTIONS_PREFERENCES_DOCUMENT_NAME]: {
    [PREFERRED_PREFERENCES_COLLECTION_NAME]: [],
  },
  [PAYMENT_PREFERENCES_DOCUMENT_NAME]: null,
};

const createUserPreferences = async <K extends keyof IUserPreferences>(
  customerUid: string,
  preferencesDocument: K,
) => {
  const preferencesRef = doc(
    firebaseService.db,
    `customers/${customerUid}/preferences`,
    preferencesDocument,
  );

  return await setDoc(preferencesRef, {});
};

export const createOrGetPreferences = async <K extends keyof IUserPreferences>(
  customerUid: string,
  preferencesDocument: K,
): Promise<UserPreferencesSubDocument[K]> => {
  const preferencesRef = doc(
    firebaseService.db,
    `customers/${customerUid}/preferences`,
    preferencesDocument,
  );
  const response = await getDoc(preferencesRef);

  if (!response.exists()) {
    await createUserPreferences(customerUid, preferencesDocument);

    return DEFAULT_PREFERENCES[preferencesDocument];
  }

  const answers = response.data();

  if (answers) {
    return answers as UserPreferencesSubDocument[K];
  }

  return DEFAULT_PREFERENCES[preferencesDocument];
};

export const getUserPreferences = async <K extends keyof IUserPreferences>(
  customerUid: string,
  preferencesDocument: K,
): Promise<UserPreferencesSubDocument[K]> => {
  const preferencesRef = doc(
    firebaseService.db,
    `customers/${customerUid}/preferences`,
    preferencesDocument,
  );
  const response = await getDoc(preferencesRef);

  if (!response.exists()) {
    return DEFAULT_PREFERENCES[preferencesDocument];
  }

  const preferences = response.data();

  if (preferences) {
    return preferences as UserPreferencesSubDocument[K];
  }

  return DEFAULT_PREFERENCES[preferencesDocument];
};

export const saveUserPreferences = async <K extends keyof IUserPreferences>(
  customerUid: string,
  preferencesDocument: K,
  data: NonNullable<IUserPreferences[K]>,
): Promise<void> => {
  const preferencesRef = doc(
    firebaseService.db,
    `customers/${customerUid}/preferences`,
    preferencesDocument,
  );

  await updateDoc(preferencesRef, data);
};
